import React, {useEffect, useState, useMemo, useRef} from 'react';
import includes from 'lodash/includes';
import orderBy from 'lodash/orderBy';
import TableRow from './TableRow';
import {ColumnType, ExtraHeadingColumn, ITableProps, SortDirection} from './types';
import Icon from '../Icon';
import {useTranslation} from 'react-i18next';
import {formatNumber} from '@apps/Shared/Utils/Utils'
import {DEFAULT_LANGUAGE} from '@apps/Shared/Constants/Language'
import './Scss/table.scss';

const map = new WeakMap()
let index = 0
const getUniqueKey = (obj: object) => {
    let key = map.get(obj)
    if (!key) {
        key = 'row-key-' + index++
        map.set(obj, key)
    }
    return key
}

export const isMobileColumn = (columnKey: string, mobileColumns: string[]) => {
    return includes(mobileColumns, columnKey);
}
export const isFirstMobileColumn = (
    columnKey: string,
    mobileColumns: string[]
) => {
    return (columnKey === mobileColumns[0]);
}
export const isLastMobileColumn = (
    columnKey: string,
    mobileColumns: string[]
) => {
    return (columnKey === mobileColumns[mobileColumns.length - 1]);
}

const getSortedData = (data: any, sortKey: string, direction: string) => {
    const sorter = (item: any) => {
        const sortItem = item[sortKey];
        if (typeof sortItem === 'number') {
            return sortItem;
        }
        if (sortItem && sortItem.toLowerCase) {
            // Make it lower case when sorting
            return sortItem.toLowerCase();
        }
        return sortItem || item;
    }
    return orderBy(data, [sorter], direction as SortDirection);
}

const Table = (props: ITableProps) => {
    const {t, i18n} = useTranslation()
    const currentLang = (i18n && i18n.language) || DEFAULT_LANGUAGE

    const [sortedData, setSortedData] = useState(
        getSortedData(props.data, props.defaultSortKey, props.defaultDirection)
    );
    const [direction, setDirection] = useState(props.defaultDirection);
    const [sortKey, setSortKey] = useState(props.defaultSortKey || '');
    const isExpandable = (props.columns && 2 < props.columns.length);
    const breakpointMobileClass = props.breakpointMobile ?? 'md';

    const rowColorSchemeClass =
    props.rowColorScheme === 'lightblue'
        ? 'table-wrapper--lightblue'
        : '';

    useEffect(() => {
        // If data is updated from outside the table component, we update it.
        setSortedData(getSortedData(props.data, sortKey, direction))
    }, [props.data]);

    const onSort = (newSortKey: string) => {
        const sortDirection = direction === 'desc' ? 'asc' : 'desc';
        const newSortedData = getSortedData(
            sortedData,
            newSortKey,
            sortDirection
        );
        setSortedData(newSortedData);
        setSortKey(newSortKey);
        setDirection(sortDirection);
    }

    const renderHeader = (header: ColumnType) => {
        const isMobileCol = isMobileColumn(header.key, props.columnsMobile);
        const isFirstMobileCol = isFirstMobileColumn(
            header.key,
            props.columnsMobile
        );
        const isLastMobileCol = isLastMobileColumn(
            header.key,
            props.columnsMobile
        );

        const firstMobileColClass = isFirstMobileCol ? 'first-column' : '';
        const lastMobileColClass = isLastMobileCol ? 'last-column' : '';
        const visibleOnMobileClass = isMobileCol ? 'visible-on-mobile' : '';
        const expandableClass = isExpandable ? 'expandable' : '';
        const renderKey = `header_${header.key}`;
        if (header.sortable) {
            const sortedClass = sortKey === header.key ? 'sorted' : '';
            const sortIconName = direction === 'asc' ? 'arrow-down' : 'arrow-up';
            return (
                <th
                    key={renderKey}
                    scope="col"
                    onClick={() => onSort(header.key)}
                    className={`sortable ${sortedClass} ${header.key} ${header.className} ${visibleOnMobileClass} ${firstMobileColClass} ${lastMobileColClass} ${expandableClass} ${breakpointMobileClass}`}
                >
                    <span className="header-text">
                        {header.text}
                        <Icon iconName={sortIconName} inline={true} />
                    </span>
                </th>
            );
        }
        return (
            <th
                key={renderKey}
                scope="col"
                className={`${header.key} ${header.className} ${visibleOnMobileClass} ${firstMobileColClass} ${lastMobileColClass} ${expandableClass} ${breakpointMobileClass}`}
            >
                {header.text}
            </th>
        );
    }
    
    const renderExtraHeadingColumns = (column: ExtraHeadingColumn, index: number) => {
        if (column === null)
        {
            return <th key={`extra-heading-column-${index}`} scope="col" className={`${breakpointMobileClass}`}></th>;
        }
        const isMobileCol = column.visibleInMobile;
        const visibleOnMobileClass = isMobileCol ? 'visible-on-mobile' : '';
        return (
            <th
                key={`extra-heading-column-${index}`}
                scope="col"
                colSpan={column.colspan ?? 1}
                className={`${column.className} ${visibleOnMobileClass} ${breakpointMobileClass}`}
            >
                {column.text}
            </th>
        )
    }

    const renderRow = (item: {[key: string]: any;}, index: number) => {
        let rowId = getUniqueKey(item);
        if (props.uniqueKey && item[props.uniqueKey]) {
            rowId = item[props.uniqueKey];
        }
        const isLastRow = !props.footer && !props.summary && index === sortedData.length - 1;
        const rowClassName = props.rowClassName ? props.rowClassName(item) : '';
        return (
            <TableRow
                key={rowId}
                rowId={rowId}
                rowData={item}
                columns={props.columns}
                columnsMobile={props.columnsMobile}
                className={`${isLastRow ? 'last-row' : ''} ${
                    props.skinnyRows ? 'skinny' : ''
                } ${rowClassName}`}
                renderForServer={props.renderForServer || false}
                breakpointMobile={props.breakpointMobile}
                extraHeadingRow={props.extraHeadingRow}
            />
        );
    }

    const renderSummaryRow = useMemo(() => {
        if (!props.summary || sortedData.length === 0) {
            return null;
        }

        return props.columns.map((column, index) => {
            if (index === 0) {
                // in first item, render single td for first column for lg
                // And all items for mobile view
                return (
                <>
                    <td key="table-summary-lg" className={`table-summary-lg table-column lg ${!props.footer ? 'first-column' : ''}`}>
                        {t('table.footer.total')}
                    </td>
                    
                    <td key="table-summary-mobile" colSpan={props.columns.length - 1} className={`table-summary-mobile table-column summary-mobile visible-on-mobile lg ${!props.footer ? 'first-column last-column' : ''}`}>
                        <div className="table-summary-mobile-inner">
                            {t('table.footer.total')}
                            {props.columns.map((column, innerIndex) => {
                                if (column.includeInSummary && innerIndex !== 0) {
                                    const total = sortedData.reduce((sum, row) => {
                                        if (column.summaryKeyName) {
                                            // For items with different key name for summary calculation
                                            return sum + (Number(row[column.summaryKeyName]) || 0);
                                        } else {
                                            return sum + (Number(row[column.key]) || 0);
                                        }
                                    }, 0);

                                    return (
                                        <div>
                                            <span className="summary-mobile-key">{column.text}: </span>
                                            <span className="summary-mobile-value">{formatNumber(total, currentLang)}</span>
                                        </div>
                                    );
                                }
                            })}

                        </div>
                    </td>
                </>);
            }
            if (column.includeInSummary) {
                const total = sortedData.reduce((sum, row) => {
                    if (column.summaryKeyName) {
                        // For items with different key name for summary calculation
                        return sum + (Number(row[column.summaryKeyName]) || 0);
                    } else {
                        return sum + (Number(row[column.key]) || 0);
                    }
                }, 0);

                return (
                    <td key={column.key} className={`table-column summary ${column.key}-summary lg ${!props.footer && index === props.columns.length -1 ? 'last-column' : ''}`}>
                        <span className="summary-value">{formatNumber(total, currentLang)}</span>
                    </td>
                );
            }

            return (<td key={column.key} className={`table-column summary summary-empty ${column.key}-summary lg ${!props.footer && index === props.columns.length -1 ? 'last-column' : ''}`}></td>);
        });
    }, [sortedData, props.columns]);

    return (
        <div className={`table-wrapper ${rowColorSchemeClass}`}>
            <table>
                <caption className={props.hideCaption ? 'sr-only' : ''}>
                    {props.caption}
                </caption>
                <thead className={`table-wrapper__thead ${breakpointMobileClass} ${props.extraHeadingRow ? 'table-wrapper__thead--extra-row' : ''} ${props.stickyHeader ? 'table-wrapper__thead--sticky' : ''}`}>
                {
                    props.extraHeadingRow && (
                        <tr className={`table-row table-row__extra-heading ${props.skinnyRows ? 'skinny' : ''}`}>
                            {props.extraHeadingRow.map((column, index) => renderExtraHeadingColumns(column, index))}
                        </tr>
                    )
                }
                <tr className={`table-row ${props.skinnyRows ? 'skinny' : ''}`}>
                    {props.columns.map(column => renderHeader(column))}
                </tr>
                </thead>
                <tbody className={breakpointMobileClass}>
                    {sortedData.map((rowData, index) =>
                        renderRow(rowData, index)
                    )}
                    {(!sortedData || sortedData.length < 1) && (
                        <tr
                            className={`table-row ${!props.footer && !props.summary ? 'last-row' : ''} ${props.skinnyRows ? 'skinny' : ''} table-nodata-row`}>
                            <td colSpan={props.columns.length}>
                                {props.noDatamessage ||
                                    t('table.nodata.default')}
                            </td>
                        </tr>
                    )}
                    {props.summary && (
                        <tr className={`table-row table-row__summary ${props.skinnyRows ? 'skinny' : ''} last-row`}>
                            {renderSummaryRow}
                        </tr>
                    )}
                    {props.footer && (
                        <tr className="table-footer-row">
                            <td colSpan={props.columns.length}>
                                {props.footer}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default Table;
